import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getPosts } from '../services/api';
import { Instagram } from 'lucide-react';
import { extractIgShortcode } from '../lib/instagram';

const softAccents = ['#F4A1C8', '#F4A1C8', '#F4A1C8', '#F4A1C8'];

const isVideo = (url) => /\.(mp4|mov|webm)$/i.test(url);

const InstagramPostsSection = () => {
  const [posts, setPosts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      const data = await getPosts();
      setPosts(data.slice(0, 8));
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // Auto-advance
  useEffect(() => {
    if (posts.length <= 1) return;
    const id = setInterval(() => {
      if (!paused) {
        setCurrentIndex(prev => (prev + 1) % posts.length);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [posts.length, paused]);

  if (posts.length === 0) return null;

  const getSlideStyle = (index) => {
    const total = posts.length;
    let diff = index - currentIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) {
      return { transform: 'translateX(0) scale(1)', opacity: 1, zIndex: 10, filter: 'none' };
    }
    if (diff === -1) {
      return { transform: 'translateX(-65%) scale(0.82)', opacity: 0.75, zIndex: 8, filter: 'none' };
    }
    if (diff === 1) {
      return { transform: 'translateX(65%) scale(0.82)', opacity: 0.75, zIndex: 8, filter: 'none' };
    }
    if (diff === -2) {
      return { transform: 'translateX(-115%) scale(0.66)', opacity: 0.45, zIndex: 6, filter: 'none' };
    }
    if (diff === 2) {
      return { transform: 'translateX(115%) scale(0.66)', opacity: 0.45, zIndex: 6, filter: 'none' };
    }
    return { transform: 'translateX(0) scale(0.3)', opacity: 0, zIndex: 0, filter: 'none' };
  };

  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{ background: 'radial-gradient(ellipse 90% 65% at 50% -5%, #E0379E 0%, rgba(224,55,158,0.55) 22%, rgba(154,38,130,0.22) 45%, rgba(116,34,99,0) 72%), linear-gradient(180deg, #4A154B 0%, #2B0A2D 100%)' }}
      data-testid="talengers-vibes-section"
    >
      <style>{`
        @keyframes softGlowPulse {
          0%, 100% {
            filter: drop-shadow(0 0 10px rgba(255,180,230,0.6)) drop-shadow(0 0 32px rgba(244,161,200,0.45)) drop-shadow(0 0 60px rgba(224,55,158,0.3));
          }
          50% {
            filter: drop-shadow(0 0 18px rgba(255,180,230,0.85)) drop-shadow(0 0 48px rgba(244,161,200,0.6)) drop-shadow(0 0 85px rgba(224,55,158,0.45));
          }
        }
        @keyframes livePulseDot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(87,255,57,0.55); }
          50% { box-shadow: 0 0 0 6px rgba(87,255,57,0); }
        }
        @keyframes haloBreathe {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50% { opacity: 0.85; transform: scale(1.08); }
        }
      `}</style>

      {/* Radiant halo directly behind the title — the "resplandor" */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none left-[5%] md:left-[12%]"
        style={{
          top: '-140px',
          width: '900px',
          height: '520px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse, #FFADE3 0%, #E0379E 35%, rgba(224,55,158,0) 70%)',
          filter: 'blur(90px)',
          animation: 'haloBreathe 5s ease-in-out infinite',
        }}
      />

      {/* Organic blurred luxury orbs for depth */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{ top: '-10%', left: '-8%', width: '480px', height: '480px', borderRadius: '50%', background: '#F4A1C8', opacity: 0.32, filter: 'blur(120px)' }}
      />
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{ bottom: '-15%', right: '-10%', width: '520px', height: '520px', borderRadius: '50%', background: '#E0379E', opacity: 0.5, filter: 'blur(130px)' }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Label */}
        <div className="flex items-center gap-3 mb-6">
          <span
            aria-hidden="true"
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: '#57FF39', animation: 'livePulseDot 1.8s ease-in-out infinite' }}
          />
          <span
            className="text-sm md:text-base font-semibold tracking-[0.3em] uppercase"
            style={{ fontFamily: "'DM Sans', sans-serif", color: '#F4A1C8' }}
          >
            En las Redes
          </span>
          <div className="flex-1 h-px max-w-[80px]" style={{ backgroundColor: '#F4A1C8' }} />
        </div>

        <h2
          data-testid="talengers-vibes-title"
          className="text-5xl md:text-7xl lg:text-8xl leading-none mb-10"
          style={{ fontFamily: "'Coolvetica', sans-serif", letterSpacing: '-0.055em', color: '#FFFDEB', animation: 'softGlowPulse 4s ease-in-out infinite' }}
        >
          <span aria-hidden="true" className="inline-block" style={{ marginRight: '-0.006em', verticalAlign: '-0.012em' }}>
            <span className="inline-block" style={{ width: '0.405em', height: '0.668em', display: 'block', backgroundColor: 'currentColor', WebkitMaskImage: 'url(/talengers-t.png)', maskImage: 'url(/talengers-t.png)', WebkitMaskSize: '100% 100%', maskSize: '100% 100%', WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat' }} />
          </span>
          <span className="sr-only">T</span>alengers Vibes
        </h2>

        {/* Glass panel wrapping the coverflow carousel */}
        <div
          className="relative rounded-[32px] p-4 sm:p-6 md:p-10 overflow-hidden"
          style={{
            background: 'rgba(255,253,235,0.04)',
            backdropFilter: 'blur(22px)',
            WebkitBackdropFilter: 'blur(22px)',
            border: '1px solid rgba(244,161,200,0.3)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,253,235,0.08), 0 -40px 100px -20px rgba(224,55,158,0.25)',
          }}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ height: 'clamp(360px, 62vw, 480px)', perspective: '1200px' }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {posts.map((post, index) => {
              const style = getSlideStyle(index);
              const isCenter = style.zIndex === 10;
              const accent = softAccents[index % softAccents.length];
              return (
                <a
                  key={post.id}
                  href={post.instagram_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute block"
                  style={{
                    width: 'clamp(220px, 60vw, 320px)',
                    height: 'clamp(310px, 84vw, 450px)',
                    transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: isCenter
                      ? `0 0 40px ${accent}55, 0 25px 55px rgba(0,0,0,0.45)`
                      : '0 12px 35px rgba(0,0,0,0.35)',
                    border: isCenter ? `3px solid ${accent}` : '1px solid rgba(244,161,200,0.25)',
                    cursor: isCenter ? 'pointer' : 'default',
                    pointerEvents: style.opacity === 0 ? 'none' : 'auto',
                    ...style,
                  }}
                  onClick={(e) => {
                    if (!isCenter) {
                      e.preventDefault();
                      setCurrentIndex(index);
                    }
                  }}
                  data-testid={`instagram-post-${index}`}
                >
                  {isVideo(post.image) ? (
                    <video
                      src={post.image}
                      className="w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={post.image}
                      alt="Instagram post"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {isCenter && (
                    <div
                      className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-opacity"
                      style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                    >
                      <Instagram size={20} color="#4B0D61" />
                    </div>
                  )}
                </a>
              );
            })}
          </div>

          {/* Dots */}
          {posts.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {posts.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Ver publicación ${idx + 1}`}
                  data-testid={`talengers-vibes-dot-${idx}`}
                  className="w-2 h-2 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: idx === currentIndex ? '#F4A1C8' : 'rgba(255,253,235,0.3)',
                    boxShadow: idx === currentIndex ? '0 0 10px rgba(244,161,200,0.7)' : 'none',
                    transform: idx === currentIndex ? 'scale(1.5)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default InstagramPostsSection;
